/**
 * 本地存储操作
 */
import { LOCAL_STORAGE_KEY as RAW_LOCAL_STORAGE_KEY, SAVE_STORAGE_VALUE as RAW_SAVE_STORAGE_VALUE } from '@/constants';
import { Base64_encode, Base64_decode, isEmptyObject, isEmptyArray } from '@/utils/api';

const LOCAL_STORAGE_KEY = {
  SAVE_STORAGE_TYPE: 'SAVE_STORAGE_TYPE',
  DO_NOT_MOVE_ANYWAY: 'DO_NOT_MOVE_ANYWAY',
  DISABLE_WAF: 'DISABLE_WAF',
  ENABLE_WAF: 'ENABLE_WAF',
  BRM_USERINFO: 'BRM_USERINFO',
  ...(RAW_LOCAL_STORAGE_KEY || {})
};
const SAVE_STORAGE_VALUE = {
  SESSION: '0',
  ...(RAW_SAVE_STORAGE_VALUE || {})
};

const { SAVE_STORAGE_TYPE } = LOCAL_STORAGE_KEY;
/**
 * cookie操作
 */
class Cookie {
  constructor() {
    this.cookie = document.cookie;
  }

  /**
   * 获取
   * @param {*} key
   * @returns
   */
  get(key) {
    this.cookie = document.cookie;
    if (!this.cookie.length) return;
    if (!key) {
      console.error(`cookie is empty or ${key} is null`);
      return null;
    }
    const cookie = this.cookie;
    const cookieArr = cookie.split(';');
    const cookieObj = {};
    cookieArr.forEach((item) => {
      const arr = item.split('=');
      cookieObj[arr[0]?.trim()] = arr[1]?.trim();
    });
    return cookieObj[key];
  }

  /**
   * 设置
   * @param {*} key
   * @param {*} value
   * @param {*} expires
   */
  set(key, value, options = {}) {
    // expires 默认7天
    const { expires = 7 * 24 * 60 * 60 * 1000, domain, path = '/' } = options;
    let expiresString = '';
    if (expires) {
      const expirationDate = new Date(Date.now() + expires).toUTCString();
      expiresString = `;expires=${expirationDate}`;
    }
    let domainString = '';
    if (domain) {
      domainString = `;domain=${domain}`;
    }
    value = typeof value === 'object' ? JSON.stringify(value) : value;
    document.cookie = `${key}=${value}${expiresString}${domainString};path=${path}`;
  }

  /**
   * 删除
   * @param {*} key
   */
  remove(key, { domain, path = '/' } = {}) {
    this.set(key, '', { expires: -1000, domain, path });
  }
}
export const cookie = new Cookie();
window.brm_cookie = cookie;

/**
 * localStorage操作
 * 支持过期时间 加密
 */
class Storage {
  constructor() {
    this.storage = null;
    this.init();
  }

  setStorage() {
    this.storage =
      window.localStorage.getItem(SAVE_STORAGE_TYPE) === SAVE_STORAGE_VALUE.SESSION
        ? window.sessionStorage
        : window.localStorage;
    return this;
  }

  init() {
    try {
      // 判断是否是iframe 合作页需要获取父窗口的localStorage
      if (window.opener) {
        this.storage =
          window.opener.localStorage.getItem(SAVE_STORAGE_TYPE) === SAVE_STORAGE_VALUE.SESSION
            ? window.opener.sessionStorage
            : window.opener.localStorage;
        // 把父窗口的localStorage存储到当前窗口
        const _storage =
          window.opener.localStorage.getItem(SAVE_STORAGE_TYPE) === SAVE_STORAGE_VALUE.SESSION
            ? window.sessionStorage
            : window.localStorage;

        Object.keys(window.opener.localStorage).forEach((key) => {
          const value = window.opener.localStorage.getItem(key);
          _storage.setItem(key, value);
        });
        return this;
      }
      return this.setStorage();
    } catch {
      return this.setStorage();
    }
  }

  /**
   * 获取
   */
  get(key, decrypt = false) {
    if (!key) {
      console.error(`storage get is empty or ${key} is null`);
      return null;
    }
    // 每次都重新获取Storage实例，避免原有实例失效, 以及解决不同tab之间的storage隔离
    this.init();

    const value = this.storage.getItem(key);
    if (!value) return null;
    const obj = JSON.parse(decrypt ? Base64_decode(value) : value);
    if (obj.expire && obj.expire < new Date().getTime()) {
      this.remove(key);
      return null;
    }
    return obj.value;
  }

  /**
   * 设置
   * @param {*} key
   * @param {*} value
   * @param {*} expire 过期时间 单位：天
   * @param { boolean } encrypt 是否加密
   * @param { boolean } isClear 是否在退出登录时清除
   */
  set(key, value, expire, encrypt = false, isClear = true) {
    if (!key) {
      console.error(`storage set is empty or ${key} is null`);
      return null;
    }
    // 被修改过存储类型 重新获取
    this.init();

    const obj = {
      value,
      expire: expire ? new Date().getTime() + expire * 24 * 60 * 60 * 1000 : null,
      isClear
    };
    this.storage.setItem(key, encrypt ? Base64_encode(JSON.stringify(obj)) : JSON.stringify(obj));
  }

  /**
   * 删除
   */
  remove(key) {
    if (!key) {
      console.error('storage remove is empty or key is null');
      return;
    }
    // 被修改过存储类型 重新获取
    this.init();
    this.storage.removeItem(key);
  }

  /**
   * 清空
   */
  clear() {
    const DO_NOT_MOVE = this.get(LOCAL_STORAGE_KEY.DO_NOT_MOVE_ANYWAY);
    // this.storage.clear();
    // 被修改过存储类型 重新获取
    this.init();
    this.handleClear();
    if (DO_NOT_MOVE) {
      this.set(LOCAL_STORAGE_KEY.DO_NOT_MOVE_ANYWAY, DO_NOT_MOVE);

      for (const key in DO_NOT_MOVE) {
        const noticeNote = storage.get(key) || {};
        for (const account in DO_NOT_MOVE[key]) {
          if (!noticeNote[account]) {
            noticeNote[account] = [];
          }
          for (const organizationId of DO_NOT_MOVE[key][account]) {
            if (noticeNote[account].indexOf(organizationId) === -1) {
              noticeNote[account].push(organizationId);
            }
            this.set(key, noticeNote);
          }
        }
      }
    }
  }

  /**
   * 清空, 只保留 isClear 为false 的值
   */
  handleClear() {
    // 遍历storage，如果 isClear 为false，则需要重新设置
    const storageObj = {};
    const allKeys = Object.values(LOCAL_STORAGE_KEY);
    // 需要保留的key
    const keepKeys = [LOCAL_STORAGE_KEY.DISABLE_WAF, LOCAL_STORAGE_KEY.ENABLE_WAF];
    // 被修改过存储类型 重新获取
    this.init();
    // lang 是通过 localStorage.setItem 直接写入的原始字符串（非 storage.set 格式），
    // 不在 allKeys 内，会被下面的 forEach 跳过，需要在 clear 前单独保存
    const lang = this.storage.getItem('lang');
    Object.keys(this.storage).forEach((key) => {
      // 判断当前key是否在全局定义过，如果没有，则跳过本次循环（防止localStorage出现一些意外的key-value）
      if (!allKeys.includes(key)) return;
      const value = this.storage.getItem(key);
      let obj = {};
      try {
        obj = JSON.parse(value ? value : '{}');
      } catch {
        try {
          // 处理加密的情况
          obj = JSON.parse(value ? Base64_decode(value) : '{}');
        } catch {
          return; // 如果解析出错，跳出本次循环
        }
      }

      if (keepKeys.includes(key)) {
        storageObj[key] = value;
      }

      if (obj?.isClear === false) {
        storageObj[key] = value;
      }
    });
    this.storage.clear();
    Object.keys(storageObj).forEach((key) => {
      this.storage.setItem(key, storageObj[key]);
    });
    // 恢复语言偏好设置，确保 storage.clear() 不会丢失用户的语言切换
    if (lang) this.storage.setItem('lang', lang);
  }

  /**
   * 设置存储类型
   * @param {*} type 1-> localStorage 0-> sessionStorage
   */
  setStorageType(type) {
    if (!type) {
      console.error('storage type is null');
      return;
    }
    this.storage = type === SAVE_STORAGE_VALUE.SESSION ? window.sessionStorage : window.localStorage;
    // 支持链式调用
    return this;
  }
}
export const storage = new Storage();
window.brm_storage = storage;

/**
 * 获取本地鉴权信息
 * @returns {
 *  menus: [],
 *  brmToken: {},
 *  keycloakToken: {},
 *  organization: {},
 *  userinfo: {}
 * }
 */
export const getStorageAuthInfo = (key, decrypt = true) => {
  return storage.get(key || LOCAL_STORAGE_KEY.BRM_USERINFO, decrypt);
};

/**
 * 获取用户id
 * @param {*} key
 * @param {*} decrypt
 * @returns
 */
export const getStorageUserId = (key, decrypt = true) => {
  const authUserInfo = getStorageAuthInfo(key, decrypt);
  return authUserInfo?.brmToken?.userId || authUserInfo?.userinfo?.id;
};

/**
 * 获取用户comeinUid
 * @param {*} key
 * @param {*} decrypt
 * @returns
 */
export const getStorageComeinUid = (key, decrypt = true) => {
  const authUserInfo = getStorageAuthInfo(key, decrypt);
  return authUserInfo?.userinfo?.comeinUid;
};

/**
 * 判断是否存在本地权限信息 作为登录凭证
 */
export const checkLogin = () => {
  try {
    const authUserInfo = getStorageAuthInfo();
    if (!authUserInfo) return false;
    const { brmToken, menus } = authUserInfo;
    if (brmToken && menus) {
      return !isEmptyObject(brmToken) && !isEmptyArray(menus);
    } else {
      return false;
    }
  } catch {
    return false;
  }
};

/**
 * 获取用户goId
 * @returns
 */
export const getStorageGoId = (key, decrypt = true) => {
  const authUserInfo = getStorageAuthInfo(key, decrypt);
  return authUserInfo?.brmToken?.goId || authUserInfo?.userinfo?.goId;
};
